import { useMemo } from 'react';
import Fuse from 'fuse.js';
import {
  useBdaasAirports,
  useOneCmsLocaleAirports,
} from './airportsFetchyeHooks';
import { useOneCmsLocaleMinimumSearchLength } from '../oneCmsIntlHooks/oneCmsIntlHelperHooks';

export const useBdaasAirportsMappedByIata = () => {
  const { data: bdaasAirportsResponse } = useBdaasAirports();

  return useMemo(() => {
    const bdaasAirports = Array.isArray(bdaasAirportsResponse?.body)
      ? bdaasAirportsResponse.body
      : [];
    return bdaasAirports.reduce((acc, airport) => {
      acc[airport.iata] = airport;
      return acc;
    }, {});
  }, [bdaasAirportsResponse]);
};

const QUERY_RULE = [
  { triggers: ['new'], city: 'new york', prefer: ['JFK', 'LGA', 'EWR'] },
  { triggers: ['lon', 'lond'], city: 'london', prefer: ['LHR'] },
  { triggers: ['los'], city: 'los angeles', prefer: ['LAX'] },
];

const matchRule = (query) => {
  const lowerCaseQuery = query.trim().toLowerCase();

  if (lowerCaseQuery.length < 3) return null;

  return QUERY_RULE.find((r) =>
    r.triggers.some((t) => lowerCaseQuery === t || lowerCaseQuery.startsWith(t))
  );
};

const rerankWithPreferredOrder = (query, results) => {
  const rule = matchRule(query);
  if (!rule) return results;

  const preferSet = new Set(rule.prefer);

  return results
    .map((r) => {
      let score = r.score ?? 1;
      const { item } = r;

      const iata = item.iata.toUpperCase();
      const city = (item.location?.city || '')?.toLowerCase();
      const desc = (item.location?.description || '')?.toLowerCase();

      if (preferSet.has(iata)) {
        score -= 0.002;
      }
      if (city === rule.city) {
        score -= 0.002;
      }

      if (desc.includes(rule.city)) {
        score -= 0.0015;
      }

      return { ...r, score };
    })
    .sort((a, b) => a.score - b.score);
};

export const useIndexedAirports = () => {
  const { data: bdaasAirportsResponse } = useBdaasAirports();

  return useMemo(() => {
    const bdaasAirports = Array.isArray(bdaasAirportsResponse?.body)
      ? bdaasAirportsResponse.body
      : [];

    // options for the fuse search
    const options = {
      keys: [
        'iata',
        'name',
        'location.city',
        'location.country_name',
        'location.description',
      ],
      includeScore: true,
      threshold: 0.3, // adjust this to make the search more or less strict
      minMatchCharLength: 3,
    };

    return new Fuse(bdaasAirports, options);
  }, [bdaasAirportsResponse]);
};

export const useTranslatedAirportResults = (query = '') => {
  const bdaasAirportsByIata = useBdaasAirportsMappedByIata();
  const indexedAirports = useIndexedAirports();
  const { data: oneCmsAirportsResponse } = useOneCmsLocaleAirports();

  const minQueryLength = useOneCmsLocaleMinimumSearchLength();

  return useMemo(() => {
    if (query.length < minQueryLength) {
      return [];
    }

    const matchingAirportsByIata = {};

    const lowerCaseQuery = query.toLowerCase();
    const oneCmsLocaleAirportsByIata = oneCmsAirportsResponse?.body ?? {};
    if (oneCmsLocaleAirportsByIata) {
      Object.entries(oneCmsLocaleAirportsByIata).forEach(
        ([iata, localeAirport]) => {
          if (
            iata?.toLowerCase()?.includes(lowerCaseQuery) ||
            localeAirport?.name?.toLowerCase()?.includes(lowerCaseQuery) ||
            localeAirport?.city?.toLowerCase()?.includes(lowerCaseQuery) ||
            localeAirport?.description?.toLowerCase()?.includes(lowerCaseQuery)
          ) {
            const bdaasAirport = bdaasAirportsByIata[iata] ?? {};
            matchingAirportsByIata[iata] = {
              ...bdaasAirport,
              iata,
              name: localeAirport?.name,
              location: {
                ...bdaasAirport?.location,
                description: localeAirport?.description,
              },
            };
          }
        }
      );
    }

    const rawResults = indexedAirports.search(query);
    return rerankWithPreferredOrder(query, rawResults).map(
      (result) => result.item
    );
  }, [
    query,
    minQueryLength,
    oneCmsAirportsResponse?.body,
    indexedAirports,
    bdaasAirportsByIata,
  ]);
};
